"use client";

import Link from "next/link";
import { ArrowRight, Check, Sparkles } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { getStarted as data } from "@/lib/data/about";

export function GetStarted() {
  return (
    <Section id="get-started" className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-[600px] w-[600px] rounded-full bg-[var(--color-crimson)] opacity-25 blur-[160px] animate-pulse-glow" />
        <div className="absolute top-1/3 right-1/4 h-72 w-72 rounded-full bg-[var(--color-cyan)] opacity-20 blur-[120px]" style={{ animationDelay: "2s" }} />
      </div>

      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-crimson)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-[1.3fr_1fr] gap-8">
          <Reveal>
            <div className="glass rounded-2xl p-7 h-full relative overflow-hidden">
              <div
                className="pointer-events-none absolute -top-32 -right-32 h-72 w-72 rounded-full opacity-20 blur-3xl"
                style={{ background: "var(--color-crimson)" }}
              />
              <div className="relative">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[var(--color-crimson)]" />
                  <span className="eyebrow-tiny text-[var(--color-crimson)]">Next Steps</span>
                </div>
                <ol className="mt-6 space-y-5">
                  {data.steps.map((s) => (
                    <li key={s.number} className="flex items-start gap-4">
                      <span
                        className="grid place-items-center h-12 w-12 rounded-2xl shrink-0 font-bold text-sm"
                        style={{
                          background: "color-mix(in oklch, var(--color-crimson) 18%, transparent)",
                          color: "var(--color-crimson)",
                          boxShadow: "inset 0 0 0 1px color-mix(in oklch, var(--color-crimson) 35%, transparent)",
                        }}
                      >
                        {s.number}
                      </span>
                      <div className="pt-1">
                        <div className="font-bold text-base">{s.title}</div>
                        <div className="text-sm text-[rgb(var(--fg-muted))] mt-1 leading-relaxed">{s.body}</div>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <div className="space-y-5">
              <div className="glass rounded-2xl p-6">
                <div className="eyebrow-tiny text-[var(--color-cyan)]">Our Promise</div>
                <ul className="mt-5 space-y-3">
                  {data.promises.map((p, i) => (
                    <li key={p} className="flex items-start gap-3 text-sm">
                      <span
                        className="grid place-items-center h-7 w-7 rounded-full shrink-0 mt-0.5"
                        style={{
                          background: "color-mix(in oklch, var(--color-cyan) 18%, transparent)",
                          boxShadow: "inset 0 0 0 1px color-mix(in oklch, var(--color-cyan) 35%, transparent)",
                        }}
                      >
                        <Check className="h-3.5 w-3.5 text-[var(--color-cyan)]" />
                      </span>
                      <span className="text-[rgb(var(--fg-muted))] leading-relaxed">{p}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex flex-col gap-3">
                <Link
                  href={data.ctaPrimary.href}
                  className="btn-primary-gradient w-full h-12 inline-flex items-center justify-center gap-2 rounded-full font-semibold text-base shadow-2xl shadow-[var(--color-crimson)/40]"
                >
                  {data.ctaPrimary.label} <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  href={data.ctaSecondary.href}
                  className="btn-glass w-full h-12 inline-flex items-center justify-center gap-2 rounded-full font-semibold text-base"
                >
                  {data.ctaSecondary.label}
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </Container>
    </Section>
  );
}
